package fr.frux.feature.map.site

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.hybridMarkerTint
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.PLAQUE_BADGE_ACCENT_COLOR
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_site_legend_ask
import fr.frux.feature.map.resources.map_site_legend_detail
import fr.frux.feature.map.resources.map_site_legend_harvest
import fr.frux.feature.map.resources.map_site_legend_open
import fr.frux.feature.map.resources.map_site_legend_private
import fr.frux.feature.map.resources.map_site_legend_title
import org.jetbrains.compose.resources.stringResource

private val SWATCH_SIZE = 20.dp
private val SWATCH_CORNER = 5.dp
private val SWATCH_GLYPH_SIZE = 14.dp

/**
 * Section « Lieux » de la legende : les trois regles d'acces et la recolte prevue, telles que les plaques les montrent
 * (meme teinte et meme glyphe que [toMapPlaques]), puis la regle du zoom de detail. Echantillons
 * carres comme les plaques, la ou les familles d'arbres sont des disques.
 */
internal fun LazyListScope.siteLegendSection() {
    item {
        Spacer(modifier = Modifier.height(FruxSpacing.sm))
        Text(
            text = stringResource(Res.string.map_site_legend_title),
            style = MaterialTheme.typography.labelLarge,
        )
    }
    item {
        val colors = MaterialTheme.fruxColors
        SiteLegendRow(
            color = hybridMarkerTint(colors.familyPomme, FruxStatus.Available, colors),
            glyph = FruxSpeciesGlyphs.Arbre,
            label = stringResource(Res.string.map_site_legend_open),
        )
    }
    item {
        val colors = MaterialTheme.fruxColors
        SiteLegendRow(
            color = hybridMarkerTint(colors.familyPomme, FruxStatus.Available, colors),
            glyph = null,
            label = stringResource(Res.string.map_site_legend_ask),
        )
    }
    item {
        SiteLegendRow(
            color = MaterialTheme.fruxColors.siteLocked,
            glyph = FruxSpeciesGlyphs.Cadenas,
            label = stringResource(Res.string.map_site_legend_private),
        )
    }
    item {
        val colors = MaterialTheme.fruxColors
        SiteLegendRow(
            color = hybridMarkerTint(colors.familyPomme, FruxStatus.Available, colors),
            glyph = FruxSpeciesGlyphs.Arbre,
            label = stringResource(Res.string.map_site_legend_harvest),
            badge = "24 oct",
        )
    }
    item {
        Text(
            text = stringResource(Res.string.map_site_legend_detail),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/** [glyph] nul : le lieu sur demande porte un « ? » en badge sur la carte, repris ici en texte. */
@Composable
private fun SiteLegendRow(
    color: Color,
    glyph: ImageVector?,
    label: String,
    badge: String? = null,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.semantics(mergeDescendants = true) {},
    ) {
        Box(
            modifier = Modifier.size(SWATCH_SIZE).background(color, RoundedCornerShape(SWATCH_CORNER)),
            contentAlignment = Alignment.Center,
        ) {
            if (glyph != null) {
                Icon(
                    imageVector = glyph,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.size(SWATCH_GLYPH_SIZE),
                )
            } else {
                Text(text = "?", style = MaterialTheme.typography.labelSmall, color = Color.White)
            }
        }
        if (badge != null) {
            Text(
                text = badge,
                style = MaterialTheme.typography.labelSmall,
                color = Color.White,
                modifier =
                    Modifier
                        .padding(start = FruxSpacing.xs)
                        .background(PLAQUE_BADGE_ACCENT_COLOR, RoundedCornerShape(SWATCH_SIZE))
                        .padding(horizontal = 6.dp, vertical = 2.dp),
            )
        }
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(text = label, style = MaterialTheme.typography.bodyMedium)
    }
}
